import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { KpiCard } from "@/components/KpiCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { ArrowLeft, Users, Milk, ShieldCheck, Ban, KeyRound, Trash2, Loader2 } from "lucide-react";

export default function TenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t: tr } = useI18n();
  const qc = useQueryClient();
  const [newPw, setNewPw] = useState("");
  const [pwOpen, setPwOpen] = useState(false);

  const { data: t, isLoading } = useQuery({
    queryKey: ["tenant", id],
    queryFn: async () => (await api.get(`/platform/tenants/${id}`)).data,
  });
  const { data: plans } = useQuery({ queryKey: ["plans"], queryFn: async () => (await api.get("/platform/plans")).data });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["tenant", id] }); qc.invalidateQueries({ queryKey: ["tenants"] }); };

  const action = useMutation({
    mutationFn: async ({ path, method = "post", body }) => (await api[method](`/platform/tenants/${id}${path}`, body)).data,
    onSuccess: () => { toast.success("Updated"); invalidate(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const changePlan = useMutation({
    mutationFn: async (plan_key) => (await api.put(`/platform/tenants/${id}`, { plan_key })).data,
    onSuccess: () => { toast.success("Plan updated"); invalidate(); },
  });

  const resetPw = useMutation({
    mutationFn: async () => (await api.post(`/platform/tenants/${id}/reset-password`, { new_password: newPw })).data,
    onSuccess: () => { toast.success("Owner password reset"); setPwOpen(false); setNewPw(""); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  if (isLoading || !t)
    return <div className="space-y-6"><Skeleton className="h-8 w-64" /><div className="grid gap-4 sm:grid-cols-3"><Skeleton className="h-28" /><Skeleton className="h-28" /><Skeleton className="h-28" /></div></div>;

  return (
    <div className="space-y-6" data-testid="tenant-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate("/platform/tenants")} className="-ml-2">
        <ArrowLeft className="mr-1.5 h-4 w-4" /> {tr("td.back")}
      </Button>
      <PageHeader
        title={t.center_name}
        subtitle={`${t.company_name} · ${t.subdomain}.milkerp.com · ${t.village || ""} ${t.district || ""} ${t.state || ""}`}
        breadcrumb={["Platform", "Tenants", t.center_name]}
        actions={
          <div className="flex flex-wrap gap-2">
            {t.effective_status === "suspended" || t.effective_status === "expired" ? (
              <Button onClick={() => action.mutate({ path: "/activate" })} data-testid="activate-tenant-btn">
                <ShieldCheck className="mr-1.5 h-4 w-4" /> {tr("td.activate")}
              </Button>
            ) : (
              <Button variant="outline" onClick={() => action.mutate({ path: "/suspend" })} data-testid="suspend-tenant-btn">
                <Ban className="mr-1.5 h-4 w-4" /> {tr("td.suspend")}
              </Button>
            )}
            <Dialog open={pwOpen} onOpenChange={setPwOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="reset-owner-pw-btn"><KeyRound className="mr-1.5 h-4 w-4" /> {tr("td.resetPassword")}</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader><DialogTitle>Reset owner password</DialogTitle></DialogHeader>
                <div className="space-y-1.5 py-2">
                  <Label>New password for {t.owner?.email}</Label>
                  <Input type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="new-owner-pw-input" />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setPwOpen(false)}>Cancel</Button>
                  <Button onClick={() => resetPw.mutate()} disabled={!newPw || resetPw.isPending} data-testid="confirm-reset-pw-btn">
                    {resetPw.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Reset
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" data-testid="delete-tenant-btn"><Trash2 className="mr-1.5 h-4 w-4" /> {tr("common.delete")}</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this tenant?</AlertDialogTitle>
                  <AlertDialogDescription>This soft-deletes the center and disables all its users. This cannot be undone from the UI.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={async () => { await api.delete(`/platform/tenants/${id}`); toast.success("Tenant deleted"); navigate("/platform/tenants"); }}
                    data-testid="confirm-delete-tenant-btn">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label={tr("common.status")} value={<span className="capitalize">{t.effective_status}</span>} icon={ShieldCheck} index={0} />
        <KpiCard label={tr("tenants.farmers")} value={t.farmer_count} icon={Users} index={1} accent="secondary" />
        <KpiCard label={tr("td.users")} value={t.user_count} icon={Users} index={2} accent="secondary" />
        <KpiCard label={tr("pd.totalCollections")} value={t.total_collections} icon={Milk} index={3} accent="warning" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="font-heading text-lg">{tr("td.subscription")}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label>{tr("td.currentPlan")}</Label>
              <Select value={t.plan_id} onValueChange={(v) => changePlan.mutate(v)}>
                <SelectTrigger data-testid="tenant-plan-change-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(plans || []).map((p) => <SelectItem key={p.key} value={p.key}>{p.name} — ₹{p.price}/mo</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Row label="Trial ends" value={(t.trial_ends_at || "").slice(0, 10)} />
            <Row label="Subscription ends" value={(t.subscription_ends_at || "").slice(0, 10)} />
            <Row label="Created" value={(t.created_at || "").slice(0, 10)} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="font-heading text-lg">{tr("td.ownerContact")}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Row label="Owner" value={t.owner_name} />
            <Row label="Email" value={t.email} />
            <Row label="Mobile" value={t.mobile} />
            <Row label="GST" value={t.gst || "—"} />
            <Row label="Address" value={`${t.address || ""} ${t.village || ""}`} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b pb-2 text-sm last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
